import { Component, OnInit } from '@angular/core';
import { ASSETS } from '@core/constants/assets.constants';
import { APP_ROUTES } from '@core/constants/routes.constants';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { CommonModule } from '@angular/common';
import { UserProfile } from '@core/models/user.model';
import { AuthService } from '@core/auth/services/auth.service';
import { USER_ROLE, Workflow_Status } from '@app/core/enums/common.enums';
import { getImageUrlWithCacheBust } from '@shared/utils/image-url.utils';

interface MenuItem {
  label: string;
  icon: string;
  routerLink: string;
  ROLES: string[];
}

@Component({
  selector: 'app-main-header',
  imports: [CommonModule, RouterLink, RouterLinkActive],
  templateUrl: './main-header.html',
  styleUrl: './main-header.scss',
})
//todo: cleanup code
export class MainHeader implements OnInit {
  ASSETS = ASSETS;
  APP_ROUTES = APP_ROUTES;
  readonly getImageUrlWithCacheBust = getImageUrlWithCacheBust;
  isAuthenticated = false;
  firstName = 'John';
  lastName = 'Doe';
  useProfilePicture = true;
  profilePicturePath = ASSETS.background.AUTH.LOGIN.BACKGROUND;

  userProfile: UserProfile | null | undefined = null;
  profilePictureUrl: string | null = null;
  accessDashboard = false;
  subscribed = false;

  constructor(
    private authService: AuthService,
    private router: Router,
  ) {}

  ngOnInit() {
    this.authService.userProfile$.subscribe((user) => {
      this.userProfile = user;
      this.profilePictureUrl = user?.profilePicturePath
        ? getImageUrlWithCacheBust(user.profilePicturePath)
        : null;
      this.accessDashboard =
        user?.workFlowStatus === Workflow_Status.subscribedActive &&
        (user?.role === USER_ROLE.ORG_ADMIN ||
          user?.role === USER_ROLE.ORG_OWNER ||
          user?.role === USER_ROLE.ORG_APP_USER ||
          user?.role === USER_ROLE.ORG_SURVEY_USER);
      this.subscribed =
        user?.workFlowStatus === Workflow_Status.subscribedActive &&
        user?.role === USER_ROLE.ORG_OWNER;
    });
  }
  /**
   * Safe avatar URL for `<img [src]>`. Returns null when the API path would hit another
   * localhost port (stale dev URLs like `//localhost:37857/id.png`), avoiding
   * `net::ERR_CONNECTION_REFUSED` noise and broken images.
   */
  get profilePictureSrc(): string | null {
    const raw = this.userProfile?.profilePicturePath?.trim();
    if (!raw || !this.isUsableProfilePictureUrl(raw)) {
      return null;
    }
    return getImageUrlWithCacheBust(raw);
  }

  private isUsableProfilePictureUrl(raw: string): boolean {
    try {
      const normalized = raw.startsWith('//') ? `${window.location.protocol}${raw}` : raw;
      const parsed = new URL(normalized, window.location.href);
      const loopback = parsed.hostname === 'localhost' || parsed.hostname === '127.0.0.1';
      if (!loopback) {
        return true;
      }
      const defaultPort = (protocol: string) => (protocol === 'https:' ? '443' : '80');
      const urlPort = parsed.port || defaultPort(parsed.protocol);
      const pagePort = window.location.port || defaultPort(window.location.protocol);
      return urlPort === pagePort;
    } catch {
      return true;
    }
  }

  logout() {
    this.authService.logout();
    this.router.navigate([APP_ROUTES.AUTH.ROOT, APP_ROUTES.AUTH.SIGNIN]);
  }

  routeLink = {
    dashboard: '/workspace/dashboard',
    upgrade: '/workspace/upgrade',
    helpCenter: '/workspace/help-center',
    questionnaire: '/workspace/questionnaire',
    setup: '/setup',
    reports: '/workspace/reports',
    myAccount: '/my-account',
  };

  leftMenuItems: MenuItem[] = [
    {
      label: 'My Workspace',
      icon: 'bi bi-ui-checks-grid',
      routerLink: this.routeLink.questionnaire,
      ROLES: [
        USER_ROLE.ORG_ADMIN,
        USER_ROLE.ORG_OWNER,
        USER_ROLE.ORG_APP_USER,
        USER_ROLE.ORG_SURVEY_USER,
      ],
    },

    {
      label: 'Integration Settings',
      icon: 'fas fa-cog',
      routerLink: this.routeLink.setup,
      ROLES: [USER_ROLE.ORG_ADMIN, USER_ROLE.ORG_OWNER, USER_ROLE.ORG_APP_USER],
    },
    // {
    //   label: 'Reports',
    //   icon: 'fas fa-chart-bar',
    //   routerLink: this.routeLink.reports,
    //   ROLES: [
    //     USER_ROLE.ORG_ADMIN,
    //     USER_ROLE.ORG_OWNER,
    //     USER_ROLE.ORG_APP_USER,
    //     USER_ROLE.ORG_SURVEY_USER,
    //   ],
    // },
  ];

  // Check if user has access to a menu item based on role
  hasAccessToMenuItem(item: MenuItem): boolean {
    if (!this.userProfile?.role) {
      return false;
    }
    return item.ROLES.includes(this.userProfile.role);
  }

  // Get filtered menu items based on user role
  get filteredLeftMenuItems(): MenuItem[] {
    if (!this.userProfile?.role) {
      return [];
    }
    return this.leftMenuItems.filter((item) => this.hasAccessToMenuItem(item));
  }
}
